import { ChatMessages } from "@storytell/cp-client";
import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent, subscribeToBlockDeltas, subscribeToBlockStop } from "@storytell/streaming";
import { TbCheck, TbPencil, TbSquare, TbSquareCheckFilled } from "solid-icons/tb";
import { createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import {
  type AskUserData,
  type AskUserOption,
  isRecommended,
  labelWithoutRecommendation,
  parseAskUserData,
  parseAskUserResponse,
} from "./askUserSchema";

type Responses = Map<number, string | string[]>;

interface AskUserRendererProps {
  block: StreamingBlock;
}

export function AskUserRenderer(props: AskUserRendererProps) {
  const rt = useChatRuntime();
  const t = rt.t;

  const [data, setData] = createSignal<AskUserData | null>(null);
  const [isStreaming, setIsStreaming] = createSignal(true);
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [activeTab, setActiveTab] = createSignal(0);
  const [responses, setResponses] = createSignal<Responses>(new Map());

  onMount(() => {
    if (props.block.finalContent?.type === "text") {
      setData(parseAskUserData(props.block.finalContent.text));
    }
    if (!data()) {
      setData(parseAskUserData(getBlockContent(props.block.blockId)));
    }

    if (props.block.isComplete) {
      setIsStreaming(false);
    }

    const unsubscribeDelta = subscribeToBlockDeltas(props.block.blockId, (delta: string) => {
      const parsed = parseAskUserData(delta);
      if (parsed) {
        setData(parsed);
      }
    });

    const unsubscribeStop = subscribeToBlockStop(props.block.blockId, () => {
      setIsStreaming(false);
    });

    onCleanup(() => {
      unsubscribeDelta();
      unsubscribeStop();
    });
  });

  const getUserResponse = (): string | null => {
    const block = props.block;
    const message = rt.store.message(block.messageId);
    if (!message) return null;

    const chat = rt.store.chat(message.chatId);
    if (!chat?.messages) return null;

    const currentIndex = chat.messages.indexOf(message.id);
    if (currentIndex === -1) return null;

    const userMessage = chat.messages
      .slice(currentIndex + 1)
      .map((msgId) => rt.store.message(msgId))
      .find((msg) => msg?.role === ChatMessages.Role.USER);

    if (!userMessage?.prompt?.text) return null;
    return userMessage.prompt.text;
  };

  const hasUserResponded = () => getUserResponse() !== null;

  const parseUserResponseSelections = createMemo(() => {
    const selections = new Map<number, Set<string>>();

    const response = getUserResponse();
    const nd = data();
    if (!response || !nd) return selections;

    for (const answer of parseAskUserResponse(response, nd).answers) {
      selections.set(answer.questionIndex, new Set(answer.optionIds));
    }

    return selections;
  });

  const isAwaitingInput = () => {
    const d = data();
    return d?.awaitingUserInput && !isSubmitting() && !hasUserResponded();
  };

  const isQuestionAnswered = (questionIndex: number): boolean => {
    if (hasUserResponded()) {
      const parsed = parseUserResponseSelections();
      return (parsed.get(questionIndex)?.size ?? 0) > 0;
    }
    const resp = responses().get(questionIndex);
    if (resp === undefined) return false;
    if (Array.isArray(resp)) return resp.length > 0;
    return resp !== "";
  };

  const areAllQuestionsAnswered = (): boolean => {
    const nd = data();
    if (!nd) return false;
    return nd.questions.every((_, idx) => isQuestionAnswered(idx));
  };

  const isOptionSelected = (questionIndex: number, optionId: string): boolean => {
    if (hasUserResponded()) {
      const parsed = parseUserResponseSelections();
      return parsed.get(questionIndex)?.has(optionId) ?? false;
    }
    const resp = responses().get(questionIndex);
    if (resp === undefined) return false;
    if (Array.isArray(resp)) return resp.includes(optionId);
    return resp === optionId;
  };

  const handleOptionClick = (questionIndex: number, option: AskUserOption, multiSelect: boolean) => {
    if (isSubmitting() || hasUserResponded()) return;

    setResponses((prev) => {
      const newMap = new Map(prev);
      if (multiSelect) {
        const current = (newMap.get(questionIndex) as string[] | undefined) ?? [];
        if (current.includes(option.id)) {
          newMap.set(
            questionIndex,
            current.filter((id) => id !== option.id)
          );
        } else {
          newMap.set(questionIndex, [...current, option.id]);
        }
      } else {
        newMap.set(questionIndex, option.id);
      }
      return newMap;
    });

    if (!multiSelect) {
      const nd = data();
      if (nd && questionIndex < nd.questions.length - 1) {
        setActiveTab(questionIndex + 1);
      }
    }
  };

  const handleEditOption = (e: MouseEvent, option: AskUserOption) => {
    e.stopPropagation();
    rt.actions.insertPromptText?.(option.label);
  };

  const formatResponseText = (): string => {
    const nd = data();
    if (!nd) return "";

    const lines: string[] = [];
    nd.questions.forEach((q, idx) => {
      const resp = responses().get(idx);
      if (resp === undefined) return;

      const idToLabel = (id: string) => q.options.find((o) => o.id === id)?.label ?? id;

      if (Array.isArray(resp) && resp.length > 0) {
        const labels = resp.map(idToLabel);
        lines.push(`${q.header}: ${labels.join(", ")}`);
      } else if (typeof resp === "string" && resp !== "") {
        lines.push(`${q.header}: ${idToLabel(resp)}`);
      }
    });
    return lines.join("\n\n");
  };

  const submitAllResponses = () => {
    if (!areAllQuestionsAnswered()) return;
    const sendPrompt = rt.actions.sendPrompt;
    if (!sendPrompt) return;
    setIsSubmitting(true);

    try {
      rt.actions.trackFeature?.({ feature: "chat.ask_user_reply", via: "button" });
      sendPrompt(formatResponseText());
    } catch (error) {
      console.error("Failed to submit response:", error);
      setIsSubmitting(false);
    }
  };

  const currentQuestion = createMemo(() => {
    const nd = data();
    if (!nd) return null;
    return nd.questions[activeTab()] ?? null;
  });

  return (
    <div
      data-renderer="ask-user-renderer"
      class="overflow-hidden rounded-xl border border-primary-border bg-panel text-base"
    >
      <Show
        when={data()}
        fallback={
          <Show when={isStreaming()}>
            <div class="flex items-center gap-2 p-3 text-sm text-secondary-text">
              <ProcessingSpinner class="size-4" />
              <span>{t("ask_user_loading")}</span>
            </div>
          </Show>
        }
      >
        {(nd) => (
          <>
            <Show when={nd().questions.length > 1}>
              <div class="flex flex-wrap gap-1.5 border-b border-muted-border p-2">
                <For each={nd().questions}>
                  {(q, index) => {
                    const isActive = () => activeTab() === index();
                    const isAnswered = () => isQuestionAnswered(index());
                    return (
                      <button
                        type="button"
                        onClick={() => setActiveTab(index())}
                        class={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-all ${
                          isActive()
                            ? "border-primary bg-tile font-medium text-primary-text"
                            : "border-transparent bg-tile text-secondary-text hover:border-muted-border hover:bg-tile-hover"
                        }`}
                      >
                        <Show when={isAnswered()}>
                          <TbCheck size={12} class="text-green-500" />
                        </Show>
                        {q.header}
                      </button>
                    );
                  }}
                </For>
              </div>
            </Show>

            <div class="p-3">
              <Show when={currentQuestion()}>
                {(q) => (
                  <>
                    <div class="text-sm font-medium text-primary-text">{q().question}</div>

                    <Show when={q().multiSelect}>
                      <div class="mt-0.5 text-xs text-secondary-text">{t("ask_user_select_multiple")}</div>
                    </Show>

                    <Show when={isAwaitingInput() || hasUserResponded()}>
                      <div class="mt-2 space-y-1.5">
                        <For each={q().options}>
                          {(option, index) => {
                            const isSelected = () => isOptionSelected(activeTab(), option.id);
                            const isDisabled = () => isSubmitting() || hasUserResponded();
                            const isMulti = () => q().multiSelect;

                            return (
                              <div class="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleOptionClick(activeTab(), option, isMulti())}
                                  disabled={isDisabled()}
                                  class={`flex-1 rounded-lg border px-2.5 py-1.5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-panel ${
                                    isSelected() ? "border-primary-border bg-tile" : "border-muted-border bg-tile"
                                  } ${
                                    isDisabled()
                                      ? "cursor-default opacity-60"
                                      : "cursor-pointer hover:scale-[1.005] hover:bg-tile-hover active:scale-[0.995]"
                                  }`}
                                >
                                  <div class="flex items-center gap-1.5 text-sm text-primary-text">
                                    <Show
                                      when={isMulti()}
                                      fallback={
                                        <Show when={isSelected()}>
                                          <TbCheck size={14} class="shrink-0 text-green-500" />
                                        </Show>
                                      }
                                    >
                                      <Show
                                        when={isSelected()}
                                        fallback={<TbSquare size={14} class="shrink-0 text-secondary-text" />}
                                      >
                                        <TbSquareCheckFilled size={14} class="shrink-0 text-green-500" />
                                      </Show>
                                    </Show>
                                    <Show when={!isMulti()}>
                                      <span class="text-secondary-text">{index() + 1}.</span>
                                    </Show>
                                    {labelWithoutRecommendation(option.label)}
                                    <Show when={isRecommended(option)}>
                                      <span class="rounded-full border border-muted-border bg-panel px-1.5 py-0.5 text-xs text-secondary-text">
                                        {t("ask_user_recommended")}
                                      </span>
                                    </Show>
                                  </div>
                                  <Show when={option.description}>
                                    <div
                                      class={`mt-0.5 text-xs text-secondary-text ${isMulti() ? "ml-5" : isSelected() ? "ml-5" : ""}`}
                                    >
                                      {option.description}
                                    </div>
                                  </Show>
                                </button>
                                <Show when={!isDisabled() && rt.actions.insertPromptText}>
                                  <button
                                    type="button"
                                    onClick={(e) => handleEditOption(e, option)}
                                    class="cursor-pointer rounded-lg p-1.5 text-secondary-text transition-colors hover:bg-tile-hover hover:text-primary-text"
                                    title={t("ask_user_edit_option")}
                                  >
                                    <TbPencil size={14} />
                                  </button>
                                </Show>
                              </div>
                            );
                          }}
                        </For>
                      </div>
                    </Show>
                  </>
                )}
              </Show>

              <Show when={hasUserResponded()}>
                <div class="mt-3 space-y-1 rounded-lg border border-primary-border bg-tile px-3 py-2">
                  <For
                    each={
                      getUserResponse()
                        ?.split("\n")
                        .filter((line) => line.trim()) ?? []
                    }
                  >
                    {(line) => (
                      <div class="flex items-center gap-2 text-sm text-primary-text">
                        <TbCheck size={14} class="shrink-0 text-green-500" />
                        <span>{line}</span>
                      </div>
                    )}
                  </For>
                </div>
              </Show>

              <Show when={isAwaitingInput() && nd().questions.length > 0}>
                <div class="mt-3 border-t border-muted-border pt-3">
                  <button
                    type="button"
                    onClick={submitAllResponses}
                    disabled={!areAllQuestionsAnswered() || isSubmitting() || !rt.actions.sendPrompt}
                    class={`w-full rounded-lg px-4 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-panel ${
                      areAllQuestionsAnswered() && !isSubmitting() && rt.actions.sendPrompt
                        ? "cursor-pointer bg-brand text-on-brand hover:opacity-90 active:scale-[0.98]"
                        : "cursor-not-allowed bg-tile text-secondary-text opacity-60"
                    }`}
                  >
                    <Show when={isSubmitting()} fallback={<span>{t("ask_user_submit_all")}</span>}>
                      <div class="flex items-center justify-center gap-2">
                        <ProcessingSpinner class="size-4" />
                        <span>{t("ask_user_submitting")}</span>
                      </div>
                    </Show>
                  </button>
                </div>
              </Show>
            </div>
          </>
        )}
      </Show>
    </div>
  );
}
